import { ObjectTypeEnum } from "@/types/refineRealEstate";

export type IndicativeMarketValuationApproach = "cost" | "comparableSales" | "income" | "landValue";

type SliderRangeField = "ci95_min" | "ci95_max" | "value_min" | "value_max";

type SliderFactor = {
  value?: number | null;
  base_value?: number | null;
  recommended_value?: number | null;
} & Partial<Record<SliderRangeField, number | null>>;

interface SliderRangeOptions {
  minKey: SliderRangeField;
  maxKey: SliderRangeField;
}

const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value);

export type IndicativeSubtotalInput =
  | {
      propertyType: "apartment";
      livingArea: number | null | undefined;
      factorValue: number | null | undefined;
    }
  | {
      propertyType: "house";
      preliminaryValue: number | null | undefined;
      factorValue: number | null | undefined;
    }
  | {
      propertyType: "multifamilyHouse";
      rentPerSqm: number | null | undefined;
      livingArea: number | null | undefined;
      operatingCosts: number | null | undefined;
      operatingCostsPercentage?: number | null | undefined;
      landValue: number | null | undefined;
      interestRate: number | null | undefined;
      capitalizationFactor: number | null | undefined;
      remainingUsefulLife?: number | null | undefined;
      adjustment?: number | null | undefined;
    };

export const getIndicativeSubtotal = (input: IndicativeSubtotalInput): number | null => {
  switch (input.propertyType) {
    case "apartment": {
      const { livingArea, factorValue } = input;

      if (!isFiniteNumber(livingArea) || !isFiniteNumber(factorValue)) {
        return null;
      }

      return factorValue * livingArea;
    }

    case "house": {
      const { preliminaryValue, factorValue } = input;

      if (!isFiniteNumber(preliminaryValue) || !isFiniteNumber(factorValue)) {
        return null;
      }

      return preliminaryValue * factorValue;
    }

    case "multifamilyHouse": {
      const {
        rentPerSqm,
        livingArea,
        operatingCosts,
        operatingCostsPercentage,
        landValue,
        interestRate,
        capitalizationFactor,
        remainingUsefulLife,
        adjustment = 0,
      } = input;

      if (
        !isFiniteNumber(rentPerSqm) ||
        !isFiniteNumber(livingArea) ||
        (!isFiniteNumber(operatingCosts) && !isFiniteNumber(operatingCostsPercentage)) ||
        !isFiniteNumber(landValue) ||
        !isFiniteNumber(interestRate) ||
        !isFiniteNumber(capitalizationFactor)
      ) {
        return null;
      }

      const normalizedInterestRate = interestRate > 1 ? interestRate / 100 : interestRate;
      const calculatedCapitalizationFactor =
        isFiniteNumber(remainingUsefulLife) && normalizedInterestRate !== 0
          ? Number(
              ((1 - Math.pow(1 + normalizedInterestRate, -remainingUsefulLife)) / normalizedInterestRate).toFixed(3),
            )
          : null;
      const resolvedCapitalizationFactor = calculatedCapitalizationFactor ?? capitalizationFactor;
      const normalizedOperatingCostsPercentage =
        isFiniteNumber(operatingCostsPercentage) && operatingCostsPercentage > 1
          ? operatingCostsPercentage / 100
          : operatingCostsPercentage;
      const grossIncome = rentPerSqm * livingArea * 12;
      const resolvedOperatingCosts = isFiniteNumber(normalizedOperatingCostsPercentage)
        ? grossIncome * normalizedOperatingCostsPercentage
        : isFiniteNumber(operatingCosts)
          ? operatingCosts
          : 0;
      const netIncome = grossIncome - resolvedOperatingCosts;
      const landInterest = landValue * normalizedInterestRate;
      const buildingIncome = netIncome - landInterest;
      const buildingValue = buildingIncome * resolvedCapitalizationFactor;

      return buildingValue + landValue + (isFiniteNumber(adjustment) ? adjustment : 0);
    }
  }
};

interface MixedUseIncomeBaseline {
  grossAnnualIncome?: number | null;
  netAnnualIncome?: number | null;
}

interface MixedUseSubtotalInput {
  residentialRent: number | null | undefined;
  commercialRent: number | null | undefined;
  residentialArea: number | null | undefined;
  commercialArea: number | null | undefined;
  residentialYieldRate: number | null | undefined;
  commercialYieldRate: number | null | undefined;
  operatingCosts: number | null | undefined;
  operatingCostsPercentage?: number | null;
  residentialBaseline?: MixedUseIncomeBaseline;
  commercialBaseline?: MixedUseIncomeBaseline;
  landValue: number | null | undefined;
  remainingUsefulLife: number | null | undefined;
  adjustment?: number | null;
}

const estimateMixedUseOperatingCosts = (grossIncome: number, baseline?: MixedUseIncomeBaseline): number | null => {
  const previousGrossIncome = baseline?.grossAnnualIncome;
  const previousNetIncome = baseline?.netAnnualIncome;
  if (
    !isFiniteNumber(previousGrossIncome) ||
    !isFiniteNumber(previousNetIncome) ||
    previousGrossIncome <= 0 ||
    previousNetIncome > previousGrossIncome
  ) {
    return null;
  }

  // Derive this part's operating-cost percentage from the latest API response:
  // (previous gross income - previous net income) / previous gross income.
  // For the live preview, apply that percentage to this part's edited gross income.
  // This assumes costs scale with rent. The API does not expose its fixed/variable
  // cost split or yield-dependent cost rules, so the saved result may differ.
  return (grossIncome / previousGrossIncome) * (previousGrossIncome - previousNetIncome);
};

export const getMixedUseIndicativeSubtotal = (input: MixedUseSubtotalInput): number | null => {
  const {
    residentialRent,
    commercialRent,
    residentialArea,
    commercialArea,
    residentialYieldRate,
    commercialYieldRate,
    remainingUsefulLife,
  } = input;

  if (
    !isFiniteNumber(residentialRent) ||
    !isFiniteNumber(commercialRent) ||
    !isFiniteNumber(residentialArea) ||
    !isFiniteNumber(commercialArea) ||
    !isFiniteNumber(residentialYieldRate) ||
    !isFiniteNumber(commercialYieldRate) ||
    !isFiniteNumber(remainingUsefulLife) ||
    residentialRent < 0 ||
    commercialRent < 0 ||
    residentialArea < 0 ||
    commercialArea < 0 ||
    residentialYieldRate < 0 ||
    commercialYieldRate < 0 ||
    remainingUsefulLife <= 0 ||
    !isFiniteNumber(input.landValue)
  ) {
    return null;
  }

  // The API rounds monthly rent per square metre before deriving annual income.
  const residentialIncome = Number(residentialRent.toFixed(2)) * residentialArea * 12;
  const commercialIncome = Number(commercialRent.toFixed(2)) * commercialArea * 12;
  const grossIncome = residentialIncome + commercialIncome;
  if (grossIncome <= 0) {
    return null;
  }

  const unroundedYieldRate =
    (residentialIncome * residentialYieldRate + commercialIncome * commercialYieldRate) / grossIncome / 100;
  // Match the observed ProsaWeb rounding: percentage and multiplier to three
  // decimals, then building income, building value, and total to whole euros.
  const weightedYieldRate = Number((unroundedYieldRate * 100).toFixed(3)) / 100;
  const capitalizationFactor =
    weightedYieldRate === 0
      ? remainingUsefulLife
      : Number(((1 - Math.pow(1 + weightedYieldRate, -remainingUsefulLife)) / weightedYieldRate).toFixed(3));
  const residentialCosts = estimateMixedUseOperatingCosts(residentialIncome, input.residentialBaseline);
  const commercialCosts = estimateMixedUseOperatingCosts(commercialIncome, input.commercialBaseline);
  const operatingCosts =
    residentialCosts != null && commercialCosts != null
      ? residentialCosts + commercialCosts
      : isFiniteNumber(input.operatingCostsPercentage)
        ? (grossIncome * input.operatingCostsPercentage) / 100
        : input.operatingCosts;
  if (!isFiniteNumber(operatingCosts) || operatingCosts < 0) {
    return null;
  }
  const buildingIncome = Math.round(grossIncome - operatingCosts - input.landValue * weightedYieldRate);
  const buildingValue = Math.round(buildingIncome * capitalizationFactor);
  const adjustment = isFiniteNumber(input.adjustment) ? input.adjustment : 0;

  return Math.round(buildingValue + input.landValue + adjustment);
};

export const getCalculatedSliderRange = (
  referenceValue: number | null | undefined,
  currentValue: number | null | undefined,
  maxLimit = Infinity,
): { min?: number; max?: number } => {
  if (referenceValue == null) return {};

  return {
    min: Math.max(0.001, Math.min(referenceValue * 0.8, currentValue ?? referenceValue)),
    max: Math.min(maxLimit, Math.max(0.002, referenceValue * 1.2, currentValue ?? referenceValue)),
  };
};

export const getSliderRange = (
  factor: SliderFactor | null | undefined,
  { minKey, maxKey }: SliderRangeOptions,
): { min: number; max: number; value: number } => {
  const factorValue = factor?.value ?? 0;
  const responseMin = factor?.[minKey];
  const responseMax = factor?.[maxKey];
  const recommendedValue = factor?.recommended_value;
  if (
    responseMin != null &&
    responseMax != null &&
    (responseMin !== responseMax || (isFiniteNumber(recommendedValue) && recommendedValue !== responseMin))
  ) {
    return {
      min: isFiniteNumber(recommendedValue) ? Math.min(responseMin, recommendedValue) : responseMin,
      max: isFiniteNumber(recommendedValue) ? Math.max(responseMax, recommendedValue) : responseMax,
      value: factorValue,
    };
  }

  return {
    min: 0.001,
    max: 9.999,
    value: factorValue,
  };
};

export const getIndicativeMarketValuationApproach = (
  objectType?: ObjectTypeEnum,
): IndicativeMarketValuationApproach => {
  switch (objectType) {
    case ObjectTypeEnum.apartment:
      return "comparableSales";
    case ObjectTypeEnum.multiFamilyHouse:
    case ObjectTypeEnum.mixedUseProperty:
      return "income";
    case ObjectTypeEnum.plotOfLand:
      return "landValue";
    default:
      return "cost";
  }
};
